import PrimarySecondary from '@common/ui/PrimarySecondary';
import { Box, Button, Grid, Skeleton, Stack, styled } from '@mui/material';
import { StandingAccessReportAppModel } from '@api';
import { DateTime } from '@utils';

import AssessmentScore from './AssessmentScore';
import AssessmentProgress from './AssessmentProgress';
import { PropsWithChildren, useMemo } from 'react';
import AssessmentStatus from './AssessmentStatus';
import { generatePath } from 'react-router-dom';
import { RouterLink } from '@libs';
import { AssessmentReportStatus, ReportTierOptions } from '@StandingAccess/types';
import { routes } from '@routes';
import useReportScore, { getTierSummary } from '@StandingAccess/hooks/use-report-score';

interface AssessmentReportItemIntegration {
  name: string;
  type: string;
  typeIcon: string;
}

interface AssessmentReportItemProps {
  report: StandingAccessReportAppModel;
  integration?: AssessmentReportItemIntegration;
  small?: boolean;
}

// Styled Components
const ReportItemWrapper = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.border.default}`,
  borderRadius: theme.shape.borderRadius,
}));

const ReportItemHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.background.paper,
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
}));

const ReportItemBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
}));

const ReportItemSmallWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.background.drawer,
  borderRadius: theme.shape.borderRadius * 2,
}));

function ReportHeader({ score, createdAt, assessedAt }: { score?: number; createdAt: string; assessedAt?: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      {score !== undefined && <AssessmentScore value={score} />}
      <PrimarySecondary primary={createdAt} secondary="Created at" inverse />
      {assessedAt && <PrimarySecondary primary={assessedAt} secondary="Last assessed at" inverse />}
    </Stack>
  );
}

function ReportBody({
  integration,
  progress,
  numberOfEntitlements,
  numberOfIdentities,
}: {
  integration?: AssessmentReportItemIntegration;
  progress?: StandingAccessReportAppModel['progress'];
  numberOfEntitlements?: number;
  numberOfIdentities?: number;
}) {
  return (
    <Grid container alignItems="center">
      <Grid item xs={9}>
        <Grid container alignItems="center" spacing={2}>
          {integration && (
            <Grid item xs={4}>
              <PrimarySecondary
                primary={integration.name}
                secondary="Integration"
                icon={integration.typeIcon}
                iconIsPrimary
                iconSize={18}
                inverse
              />
            </Grid>
          )}
          {numberOfEntitlements !== undefined && (
            <Grid item xs={3}>
              <PrimarySecondary primary={`${numberOfEntitlements}`} secondary="Number of entitlements" inverse />
            </Grid>
          )}
          {numberOfIdentities !== undefined && (
            <Grid item xs={3}>
              <PrimarySecondary primary={`${numberOfIdentities}`} secondary="Number of identities" inverse />
            </Grid>
          )}
        </Grid>
      </Grid>
      {progress && progress.total_steps !== progress.step && (
        <Grid item xs={3}>
          <AssessmentProgress progress={progress} />
        </Grid>
      )}
    </Grid>
  );
}

export function AssessmentReportItemActions({ report }: { report: StandingAccessReportAppModel }) {
  const isDisabled =
    report.status.status === AssessmentReportStatus.InProgress ||
    report.status.status === AssessmentReportStatus.Error ||
    report.status.status === AssessmentReportStatus.ReAssess;

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <AssessmentStatus reportStatus={report.status} />
      <Button
        component={RouterLink}
        to={generatePath(routes.ViewStandingAccessReport.path, { id: report.id })}
        variant="outlined"
        color="secondary"
        disabled={isDisabled}
      >
        Explore
      </Button>
    </Stack>
  );
}

export default function AssessmentReportItem({
  report,
  integration,
  small,
  children,
}: PropsWithChildren<AssessmentReportItemProps>) {
  const createdAt = DateTime.readable(report.created_date);
  const assessedAt = report.assessed_at ? DateTime.readable(report.assessed_at) : undefined;
  const { score } = useReportScore({ report });

  if (small) {
    return (
      <ReportItemSmallWrapper>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={4}>
          <ReportHeader score={score} createdAt={createdAt} assessedAt={assessedAt} />
          <ReportBody
            integration={integration}
            progress={report.progress}
            numberOfEntitlements={report.items_counts_summary.total?.entitlements}
            numberOfIdentities={report.items_counts_summary.total?.identities}
          />
          {children}
        </Stack>
      </ReportItemSmallWrapper>
    );
  }

  return (
    <ReportItemWrapper>
      <ReportItemHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <ReportHeader score={score} createdAt={createdAt} assessedAt={assessedAt} />
          {children}
        </Stack>
      </ReportItemHeader>
      <ReportItemBody>
        <ReportBody
          integration={integration}
          progress={report.progress}
          numberOfEntitlements={report.items_counts_summary.total?.entitlements}
          numberOfIdentities={report.items_counts_summary.total?.identities}
        />
      </ReportItemBody>
    </ReportItemWrapper>
  );
}

export function AssessmentReportItemLoading() {
  return (
    <ReportItemWrapper>
      <ReportItemHeader>
        <Skeleton variant="text" width="50%" />
      </ReportItemHeader>
    </ReportItemWrapper>
  );
}

export type { AssessmentReportItemIntegration };
